import {createAsyncThunk} from '@reduxjs/toolkit'
import {
  FetchUserByIdRequest,
  UpdateUserRequest,
  FetchUsersRequest,
  CreateUserRequest,
  DeleteUserRequest,
  SuspendUserRequest,
  ActivateUserRequest,
  FetchAllUsersRequest,
  ActivateUsersExceptRequest,
  SuspendUsersExceptRequest,
} from './types'
import {AxiosError} from 'axios'
import {ResponseError} from '../commonTypes'
import {User} from '../../../models'
import axios from 'axios'
import {
  fetchUsersUrl,
  fetchAllUsersUrl,
  fetchUserByIdUrl,
  createUserUrl,
  updateUserUrl,
  deleteUserUrl,
  suspendUserUrl,
  activateUserUrl,
  activateUsersExceptUrl,
  suspendUsersExceptUrl,
} from '../../api'

export const fetchUsers = createAsyncThunk(
  'user/fetch',
  async (payload: FetchUsersRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        `${fetchUsersUrl}?sortField=${payload.sortField}&sortOrder=${
          payload.sortOrder
        }&pageNumber=${payload.pageNumber}&pageCount=${payload.pageCount}${
          payload.search && payload.search.trim() !== '' ? '&search=' + payload.search.trim() : ''
        }${payload.role ? '&role=' + payload.role : ''}`
      )
      const {users, total, totalPages} = res.data
      return {
        total,
        totalPages,
        users,
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const message = err.response?.data.fr
      return rejectWithValue({
        success: false,
        error: message || 'message',
      })
    }
  }
)

export const fetchAllUsers = createAsyncThunk(
  'users/fetchAll',
  async (payload: FetchAllUsersRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${fetchAllUsersUrl}`)
      const {users} = res.data
      return {
        users,
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const message = err.response?.data.fr
      return rejectWithValue({
        success: false,
        error: message || 'message',
      })
    }
  }
)

export const fetchUserById = createAsyncThunk(
  'user/fetchById',
  async (payload: FetchUserByIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${fetchUserByIdUrl}/${payload.id}`)
      const {user} = res.data
      return {
        user,
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const message = err.response?.data.fr
      return rejectWithValue({
        success: false,
        error: message || 'message',
      })
    }
  }
)

export const createUser = createAsyncThunk(
  'user/create',
  async (payload: CreateUserRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(createUserUrl, payload)
      const {user} = res.data
      return {
        user,
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const message = err.response?.data.fr
      return rejectWithValue({
        success: false,
        error: message || 'message',
      })
    }
  }
)

export const updateUser = createAsyncThunk(
  'user/update',
  async (payload: UpdateUserRequest, {rejectWithValue}) => {
    try {
      const data = new FormData()
      data.append('id', payload.id)
      if (payload.firstName) {
        data.append('firstName', payload.firstName)
      }
      if (payload.lastName) {
        data.append('lastName', payload.lastName)
      }
      if (payload.phone) {
        data.append('phone', payload.phone)
      }
      if (payload.email) {
        data.append('email', payload.email)
      }
      if (payload.establishements) {
        data.append('establishements', JSON.stringify(payload.establishements))
      }
      if (payload.subjects) {
        data.append('subjects', JSON.stringify(payload.subjects))
      }
      if (payload.classrooms) {
        data.append('classrooms', JSON.stringify(payload.classrooms))
      }
      if (payload.previousClasses) {
        data.append('previousClasses', JSON.stringify(payload.previousClasses))
      }

      if (payload.governorate) {
        data.append('governorate', payload.governorate)
      }

      if (payload.location) {
        data.append('location', payload.location)
      }

      if (payload.zipCode) {
        data.append('zipCode', payload.zipCode)
      }

      if (payload.cin) {
        data.append('cin', payload.cin)
      }

      if (payload.identifiantStudent) {
        data.append('identifiantStudent', payload.identifiantStudent)
      }

      if (payload.jobPosition) {
        data.append('jobPosition', payload.jobPosition)
      }

      if (payload.salary !== undefined && payload.salary !== null) {
        data.append('salary', payload.salary.toString())
      }

      // Personal Company/Enterprise data
      if (payload.personalFiscalName) {
        data.append('personalFiscalName', payload.personalFiscalName)
      }
      if (payload.personalFiscalNumber) {
        data.append('personalFiscalNumber', payload.personalFiscalNumber)
      }
      if (payload.personalPostalCode) {
        data.append('personalPostalCode', payload.personalPostalCode)
      }
      if (payload.personalCountry) {
        data.append('personalCountry', payload.personalCountry)
      }
      if (payload.personalCnssNumber !== undefined) {
        data.append('personalCnssNumber', payload.personalCnssNumber)
      }

      if (payload.birthDay) {
        data.append('birthDay', payload.birthDay)
      }

      if (payload.placeOfBirth) {
        data.append('placeOfBirth', payload.placeOfBirth)
      }

      if (payload.briefDescription) {
        data.append('briefDescription', payload.briefDescription)
      }

      if (payload.avatar) {
        payload.avatar !== 'string' && data.append('file', payload.avatar)
      }
      if (payload.deleteAvatar) {
        data.append('deleteAvatar', 'true')
      }
      if (payload.kids) {
        data.append('kids', JSON.stringify(payload.kids))
      }
      if (payload.role) {
        data.append('role', payload.role)
      }
      if (payload.subscription) {
        data.append('subscription', payload.subscription)
      }
      if (payload.startWorkingDate) {
        data.append('startWorkingDate', payload.startWorkingDate)
      }
      if (payload.endWorkingDate !== undefined) {
        data.append('endWorkingDate', payload.endWorkingDate === null ? '' : payload.endWorkingDate)
      }
      const res = await axios.post(updateUserUrl, data)
      const {user} = res.data
      return {
        user,
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const message = err.response?.data.fr
      return rejectWithValue({
        success: false,
        error: message || 'message',
      })
    }
  }
)

export const deleteUser = createAsyncThunk(
  'user/delete',
  async (payload: DeleteUserRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(deleteUserUrl, payload)
      const {user} = res.data
      return {
        user,
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const message = err.response?.data.fr
      return rejectWithValue({
        success: false,
        error: message || 'message',
      })
    }
  }
)

export const suspendUser = createAsyncThunk(
  'user/suspend',
  async (payload: SuspendUserRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(suspendUserUrl, payload)
      const {user} = res.data
      return {
        user,
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const message = err.response?.data.fr
      return rejectWithValue({
        success: false,
        error: message || 'message',
      })
    }
  }
)

export const activateUser = createAsyncThunk(
  'user/activate',
  async (payload: ActivateUserRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(activateUserUrl, payload)
      const {user} = res.data
      return {
        user,
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const message = err.response?.data.fr
      return rejectWithValue({
        success: false,
        error: message || 'message',
      })
    }
  }
)

export const activateUsersExcept = createAsyncThunk(
  'users/activate',
  async (payload: ActivateUsersExceptRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(activateUsersExceptUrl, payload)
      return {
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const message = err.response?.data.fr
      return rejectWithValue({
        success: false,
        error: message || 'message',
      })
    }
  }
)

export const suspendUsersExcept = createAsyncThunk(
  'users/suspend',
  async (payload: SuspendUsersExceptRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(suspendUsersExceptUrl, payload)
      return {
        success: true,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const message = err.response?.data.fr
      return rejectWithValue({
        success: false,
        error: message || 'message',
      })
    }
  }
)
